// Draggable scrollbar for slideshow-component (extracted from snippets/slideshow.liquid).
// Custom-element upgrade handles both parsed and dynamically-inserted elements,
// so no DOMContentLoaded bootstrap or MutationObserver is needed.
if (!customElements.get('slideshow-scrollbar')) {
  class SlideshowScrollbar extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
    }

    connectedCallback() {
      if (this.initialized) return;

      requestAnimationFrame(() => {
        this.track = this.querySelector('.scrollbar-track');
        this.thumb = this.querySelector('.scrollbar-thumb');
        this.slideshow = this.closest('slideshow-component');

        if (!this.track || !this.thumb || !this.slideshow) return;

        setTimeout(() => {
          this.scroller = this.slideshow.querySelector('slideshow-slides');
          if (!this.scroller) return;
          this.init();
        }, 300);
      });
    }

    init() {
      this.initialized = true;

      this.isDragging = false;
      this.startX = 0;
      this.startScrollLeft = 0;
      this.trackRect = null;
      this.trackWidth = this.track.offsetWidth;

      // Bind methods to maintain context
      this.updateScrollbar = this.updateScrollbar.bind(this);
      this.startDrag = this.startDrag.bind(this);
      this.onDrag = this.onDrag.bind(this);
      this.stopDrag = this.stopDrag.bind(this);
      this.jumpToPosition = this.jumpToPosition.bind(this);
      this.handleResize = this.handleResize.bind(this);

      // Update scrollbar when slideshow scrolls
      this.scroller.addEventListener('scroll', this.updateScrollbar);

      // Mouse events - only on thumb and track
      this.thumb.addEventListener('mousedown', this.startDrag);
      this.track.addEventListener('click', this.jumpToPosition);

      // Touch events
      this.thumb.addEventListener('touchstart', this.handleTouchStart.bind(this));
      this.track.addEventListener('touchstart', this.handleTrackTouch.bind(this));

      // Prevent default drag behavior on thumb
      this.thumb.addEventListener('dragstart', (e) => e.preventDefault());

      // Initial update
      this.updateScrollbar();

      // Update on resize
      window.addEventListener('resize', this.handleResize);
    }

    handleResize() {
      this.trackRect = this.track.getBoundingClientRect();
      this.trackWidth = this.track.offsetWidth;
      this.updateScrollbar();
    }

    handleTouchStart(e) {
      e.preventDefault();
      this.startDrag(e.touches[0]);
    }

    handleTrackTouch(e) {
      if (e.target === this.thumb) return;
      e.preventDefault();
      this.jumpToPosition(e.touches[0]);
    }

    updateScrollbar() {
      if (!this.scroller) return;

      const scrollWidth = this.scroller.scrollWidth;
      const clientWidth = this.scroller.clientWidth;
      const scrollLeft = this.scroller.scrollLeft;

      // Check if scrolling is possible
      if (scrollWidth <= clientWidth) {
        this.thumb.style.opacity = '0.3';
        this.thumb.style.width = '100%';
        this.thumb.style.left = '0%';
        return;
      }

      this.thumb.style.opacity = '1';

      // Calculate thumb size and position
      const visibleRatio = clientWidth / scrollWidth;
      const thumbWidth = Math.max(visibleRatio * 100, 5);
      const maxScroll = scrollWidth - clientWidth;
      const scrollPercent = maxScroll > 0 ? (scrollLeft / maxScroll) : 0;
      const maxThumbPos = 100 - thumbWidth;
      const thumbPos = scrollPercent * maxThumbPos;

      this.thumb.style.width = `${thumbWidth}%`;
      this.thumb.style.left = `${Math.max(0, Math.min(thumbPos, maxThumbPos))}%`;
    }

    startDrag(e) {
      e.preventDefault();
      e.stopPropagation();

      // Only allow dragging if clicking directly on the thumb
      if (e.target !== this.thumb && !this.thumb.contains(e.target)) {
        return;
      }

      this.isDragging = true;
      this.trackRect = this.track.getBoundingClientRect();
      this.startX = e.clientX - this.trackRect.left;
      this.startScrollLeft = this.scroller.scrollLeft;
      this.trackWidth = this.track.offsetWidth;

      // Add dragging styles
      this.thumb.style.transition = 'none';
      document.body.classList.add('slideshow-dragging');

      // Add global event listeners
      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.stopDrag);
      document.addEventListener('touchmove', this.handleTouchMove.bind(this));
      document.addEventListener('touchend', this.stopDrag);
    }

    handleTouchMove(e) {
      if (!this.isDragging) return;
      e.preventDefault();
      this.onDrag(e.touches[0]);
    }

    onDrag(e) {
      if (!this.isDragging) return;

      // Update track rect in case of layout shifts
      this.trackRect = this.track.getBoundingClientRect();
      const currentX = e.clientX - this.trackRect.left;
      const dragDistance = currentX - this.startX;

      const scrollWidth = this.scroller.scrollWidth;
      const clientWidth = this.scroller.clientWidth;
      const maxScroll = scrollWidth - clientWidth;

      if (maxScroll <= 0) return;

      // Calculate new scroll position
      const dragRatio = dragDistance / this.trackWidth;
      const scrollDistance = dragRatio * maxScroll;
      const newScrollLeft = this.startScrollLeft + scrollDistance;

      // Apply scroll with boundaries
      const clampedScroll = Math.max(0, Math.min(newScrollLeft, maxScroll));

      this.scroller.scrollTo({
        left: clampedScroll,
        behavior: 'auto'
      });
    }

    stopDrag() {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.thumb.style.transition = '';
      document.body.classList.remove('slideshow-dragging');

      // Remove global event listeners
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.stopDrag);
      document.removeEventListener('touchmove', this.handleTouchMove);
      document.removeEventListener('touchend', this.stopDrag);
    }

    jumpToPosition(e) {
      // Only allow clicking on the track, not the thumb
      if (e.target === this.thumb || this.thumb.contains(e.target)) {
        return;
      }

      this.trackRect = this.track.getBoundingClientRect();
      const clickX = e.clientX ? e.clientX : e.pageX;
      const clickXRelative = clickX - this.trackRect.left;
      const clickPercent = Math.max(0, Math.min(clickXRelative / this.trackWidth, 1));

      const scrollWidth = this.scroller.scrollWidth;
      const clientWidth = this.scroller.clientWidth;
      const maxScroll = scrollWidth - clientWidth;

      if (maxScroll <= 0) return;

      const targetScroll = clickPercent * maxScroll;

      this.scroller.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
    }

    disconnectedCallback() {
      // Cleanup
      if (this.scroller) {
        this.scroller.removeEventListener('scroll', this.updateScrollbar);
      }
      window.removeEventListener('resize', this.handleResize);
      this.initialized = false;
    }
  }

  customElements.define('slideshow-scrollbar', SlideshowScrollbar);
}
